import React, { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import DischargeReceiptModal from "./DischargeReceiptModal";
import DischargeEditModal from "./DischargeEditModal";
import { customFetch } from '../apiClient';
import * as XLSX from "xlsx";
import { useTheme } from '../context/ThemeContext'; 

// ✅ COMPACT STYLES FOR REACT SELECT (26px height)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '26px',
    height: '26px',
    fontSize: '11px',
    borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    padding: '0'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px', height: '24px' }),
  indicatorsContainer: (base) => ({ ...base, height: '24px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  input: (base) => ({ ...base, margin: '0', padding: '0', fontSize: '11px' }),
  menu: (base) => ({ ...base, zIndex: 99999, borderRadius: '4px' }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    padding: '6px 10px',
    backgroundColor: state.isSelected 
      ? 'var(--theme-color, #0088cc)' 
      : state.isFocused 
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
};

const DischargeList = () => {
  const [dischargeList, setDischargeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const [useDateFilter, setUseDateFilter] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState(""); 

  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [receiptData, setReceiptData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  
  const [companyDetails, setCompanyDetails] = useState(null);
  const [editDischargeId, setEditDischargeId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15); 
  const [totalRecords, setTotalRecords] = useState(0);

  const { themeColor } = useTheme(); 
  const token = sessionStorage.getItem("token");
  
  const recordToEdit = dischargeList.find(
    d => (d.discharge?.discHargeID || d.discHargeID) === editDischargeId
  );

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => { setSuccess(""); setError(""); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const searchOptions = [
    { value: "", label: "All Fields" },
    { value: "name", label: "Patient Name" },
    { value: "mobile", label: "Mobile No" },
    { value: "uhid", label: "UHID No" },
    { value: "srno", label: "Last 4 Digits" },
    { value: "id", label: "IPD ID" },
    { value: "billno", label: "Bill No" }
  ];

  useEffect(() => {
    const handleExportEvent = async () => {
      if (!dischargeList || dischargeList.length === 0) {
        alert("No records found to export!");
        return;
      }

      const exportData = dischargeList.map((d, index) => {
        const dis = d.discharge || d; 
        const pat = d.patient || d; 

        return {
          "Sr No": index + 1,
          "IPD No": pat.ipD_SRNO || pat.IPD_SRNO,
          "UHID": pat.uhiD_No || pat.uhidNo,
          "Patient Name": pat.name || pat.Name,
          "Mobile": pat.mobileNo || pat.MobileNo,
          "Address": pat.address || pat.Address,
          "Discharge Date": (dis.disdate || dis.disch_Date || '').split("T")[0],
          "Bill No": dis.invNo || dis.InvNo,
          "Total Amount": dis.totalAmount || dis.TotalAmount || 0,
          "Discount": dis.discount || dis.Discount || 0,
          "Payable": dis.payable || dis.Payable || 0,
          "Patient Status": (dis.patientsSTS || '').toLowerCase() === 'dead' ? 'Dead' : 'Alive'
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Discharge_List");

      const defaultFileName = `Discharge_List_${new Date().toISOString().split("T")[0]}.xlsx`;

      try {
        if (window.showSaveFilePicker) {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: defaultFileName,
            types: [{
              description: 'Excel Workbook',
              accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
            }],
          });
          const writable = await fileHandle.createWritable();
          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          await writable.write(excelBuffer);
          await writable.close();
        } else {
          XLSX.writeFile(workbook, defaultFileName);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Export Error:", error);
          alert("Error saving file!");
        }
      }
    };

    window.addEventListener("exportData", handleExportEvent);
    return () => window.removeEventListener("exportData", handleExportEvent);
  }, [dischargeList]);

  const fetchDischargeList = async (page = currentPage, rows = rowsPerPage) => {
    try {
      setLoading(true);
      setError("");

      let url = `/DischargeDt/search?page=${page}&pageSize=${rows}&useDateFilter=${useDateFilter}&`;
      
      if (useDateFilter) {
        if (fromDate) url += `from=${fromDate}&`;
        if (toDate) url += `to=${toDate}&`;
      }
      
      if (searchText) {
        url += `search=${encodeURIComponent(searchText)}&`;
        if (searchType) {
          url += `searchType=${searchType}&`;
        }
      }

      if (selectedDepartment) url += `departmentId=${selectedDepartment.value}&`;
      if (selectedDoctor) url += `doctorId=${selectedDoctor.value}&`;

      url = url.replace(/[&?]$/, "");

     const res = await customFetch(url);
      if (!res.ok) throw new Error("Failed to fetch discharge records");
      const data = await res.json();
      
      setDischargeList(data.data || data || []);
      setTotalRecords(data.totalRecords || (data.data ? data.data.length : data.length) || 0);
      setCurrentPage(page);
    } catch (err) {
      console.error("Error:", err);
      setError(err.message);
      setDischargeList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchTypeChange = (selectedOption) => {
    setSearchType(selectedOption ? selectedOption.value : "");
    handleFilterChange();
  };

  const handleFilterChange = () => { setCurrentPage(1); };

  const fetchDepartments = useCallback(async () => {
    try {
     const res = await customFetch(`/Departments`);
      if (!res.ok) throw new Error("Failed to load departments");
      const data = await res.json();
      setDepartments(data ? data.map(d => ({ value: d.srno, label: d.departmentName })) : []);
    } catch (err) { console.error(err.message); }
  }, [token]);

  const fetchDoctors = useCallback(async () => {
    try {
      const res = await customFetch(`/Employees/doctors`);
      if (!res.ok) throw new Error("Failed to load doctors");
      const data = await res.json();
      setDoctors(data ? data.map(d => ({ value: d.id, label: d.name })) : []);
    } catch (err) { console.error(err.message); }
  }, [token]);

  useEffect(() => { fetchDepartments(); fetchDoctors(); }, [fetchDepartments, fetchDoctors]);

  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    if (hospitalId) {
      customFetch(`/Company/${hospitalId}`)
      .then((res) => res.json())
      .then((data) => setCompanyDetails(data))
      .catch(console.error);
    }
  }, [token]);

  useEffect(() => {
    fetchDischargeList(1, rowsPerPage || 15);
  }, [fromDate, toDate, searchText, searchType, useDateFilter, selectedDepartment, selectedDoctor]);

  const handleDateChange = (type, e) => {
    if(type === 'from') setFromDate(e.target.value);
    else setToDate(e.target.value);
    handleFilterChange();
  };

  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
    handleFilterChange();
  };

  const handleRowsPerPageChange = (e) => {
    const val = Number(e.target.value);
    setRowsPerPage(val);
    fetchDischargeList(1, val);
  };

  const handleEditClick = (row) => {
    const id = row.discharge?.discHargeID || row.discHargeID || row.DiscHargeID;
    if(id) {
      setEditDischargeId(id);
      setIsEditModalOpen(true);
    } else {
      setError("Invalid Record ID");
    }
  };

  const handleSaveEdit = async (updatedData) => {
    try {
      const id = updatedData.discHargeID || updatedData.DiscHargeID;
     const res = await customFetch(`/DischargeDt/${id}`, {
  method: "PUT",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ ...updatedData, patientsSTS: updatedData.patDieOrLive ? "Dead" : "Alive" }),
});

      if (res.ok) {
        setSuccess("Record updated successfully");
        setIsEditModalOpen(false);
        fetchDischargeList(); 
      } else {
        const text = await res.text();
        throw new Error(text);
      }
    } catch (e) { setError("Update failed: " + e.message); }
  };

  const handleDelete = async (ipdId) => {
    if (!window.confirm("Are you sure to delete this discharge record?")) return;
    try {
      const res = await customFetch(`/DischargeDt/delete-by-ipd/${ipdId}`, {
  method: "DELETE" 
});
      if (res.ok) {
        setSuccess("Record deleted successfully");
        fetchDischargeList();
      } else { throw new Error("Failed to delete"); }
    } catch (err) { setError(err.message); }
  };

  const handlePreview = async (row) => {
    const dischargeId = row.discharge?.discHargeID || row.discHargeID;
    if (!row.serviceDetails && !row.discharge?.serviceDetails) {
        setLoading(true);
        try {
           const res = await customFetch(`/DischargeDt/receipt/${dischargeId}`);
            if(res.ok) {
                const fullData = await res.json();
                prepareReceipt(fullData);
            } else { prepareReceipt(row); }
        } catch(e) { console.error(e); prepareReceipt(row); 
        } finally { setLoading(false); }
    } else { prepareReceipt(row); }
  };
const prepareReceipt = (data) => {
    const discharge = data.discharge || data.dischargeDetails || data;
    const patient = data.patient || data.patientDetails || (discharge.patientDetails) || {};
    
    const formattedData = {
        billDate: discharge.invDates || discharge.InvDates,
        dischargeDate: discharge.disdate || discharge.Disdate || discharge.disch_Date,
        createdBy: discharge.createdBy || discharge.CreatedBy,
        
        patient: {
            name: patient.name || patient.Name || discharge.name || discharge.Name,
            uhidNo: patient.uhidNo || patient.UHID_No || patient.uhiD_No || discharge.uhidNo || discharge.uhiD_No,
            mobileNo: patient.mobileNo || patient.MobileNo || discharge.mobileNo,
            address: patient.address || patient.Address || discharge.address,
            age: patient.age || discharge.age,
            careOff: patient.careOff || patient.CareOff || "-", 
            careOfMobile: patient.alternateMobileNo || patient.careOfMobile || "-",
            gender: patient.gender || discharge.gender,
            ipdNo: patient.ipd_SRNO || patient.IPD_SRNO || discharge.ipD_SRNO
        },

        // ✅ FIX: .map() हटा दिया गया है। अब ओरिजिनल डेटा सीधे रसीद में जाएगा
        // जिससे रसीद को roomCatge, roomNo और roomRate आसानी से मिल जाएंगे
        services: data.serviceDetails || data.services || [],
        roomDetails: data.roomDetails || data.rooms || [],
        advanceDetails: data.advanceDetails || data.advance || [],

        totalAmount: discharge.totalAmount || discharge.TotalAmount || 0,
        discount: discharge.discount || discharge.Discount || 0,
        payable: discharge.payable || discharge.Payable || 0,
        received: discharge.received || discharge.Received || 0,
        due: discharge.due || discharge.Due || 0, 
        
        rawServerResponse: data
    };

    setReceiptData(formattedData);
    setShowReceipt(true);
  };
  const totalPages = Math.ceil(totalRecords / (rowsPerPage || 1));
  
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
        if(i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
            pages.push(
                <button 
                    key={i} 
                    className={`dc-btn-page ${currentPage === i ? "active" : ""}`}
                    onClick={() => fetchDischargeList(i, rowsPerPage)}
                >
                    {i}
                </button>
            );
        } else if (pages[pages.length-1]?.key !== "...") {
            pages.push(<span key="..." className="dc-dots">...</span>);
        }
    }
    return (
      <div className="dc-pagination">
        {pages}
      </div>
    );
  };

  return (
    <div className="dc-wrap">
      <div className="dc-card">
        
        {/* ✅ COMPACT HEADER */}
        <div className="dc-header-compact">
          <div className="dc-header-brand-compact">
            <div className="dc-header-icon-compact"><i className="bi bi-file-medical"></i></div>
            <div className="dc-header-text-compact">
              <h5>Discharge Patients List</h5>
              <p>View and manage discharged patient records</p>
            </div>
          </div>
        </div>

        <div className="dc-body">
          {error && <div className="dc-alert dc-alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
          {success && <div className="dc-alert dc-alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          {/* Compact Filters Section */}
          <div className="dc-section dc-mb2">
            <div className="dc-section-body" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              
              {/* Checkbox for Date Filter */}
              <label className="dc-checkbox-label">
                <input type="checkbox" checked={useDateFilter} onChange={(e) => { setUseDateFilter(e.target.checked); handleFilterChange(); }} />
                Apply Date Filter
              </label>

              <div className="dc-filter-grid">
                <div>
                  <label className="dc-label">From Date</label>
                  <input type="date" className="dc-input" value={fromDate} onChange={(e) => handleDateChange('from', e)} disabled={!useDateFilter} style={{ opacity: !useDateFilter ? 0.5 : 1 }} />
                </div>
                <div>
                  <label className="dc-label">To Date</label>
                  <input type="date" className="dc-input" value={toDate} onChange={(e) => handleDateChange('to', e)} disabled={!useDateFilter} style={{ opacity: !useDateFilter ? 0.5 : 1 }} />
                </div>
                <div>
                  <label className="dc-label">Department</label>
                  <Select options={departments} value={selectedDepartment} onChange={(opt) => { setSelectedDepartment(opt); handleFilterChange(); }} styles={selectStyles} placeholder="All Depts" isClearable menuPortalTarget={document.body} />
                </div>
                <div>
                  <label className="dc-label">Doctor</label>
                  <Select options={doctors} value={selectedDoctor} onChange={(opt) => { setSelectedDoctor(opt); handleFilterChange(); }} styles={selectStyles} placeholder="All Doctors" isClearable menuPortalTarget={document.body} />
                </div>
                <div>
                  <label className="dc-label">Search By</label>
                  <Select options={searchOptions} value={searchOptions.find(opt => opt.value === searchType)} onChange={handleSearchTypeChange} styles={selectStyles} placeholder="All Fields" isSearchable={false} menuPortalTarget={document.body} />
                </div>
                <div style={{ flexGrow: 2, minWidth: '150px' }}>
                  <label className="dc-label">Search Text</label>
                  <input type="text" className="dc-input" value={searchText} onChange={handleSearchTextChange} placeholder="Type here..." />
                </div>
                <div style={{ maxWidth: '70px' }}>
                  <label className="dc-label">Rows</label>
                  <select className="dc-input" value={rowsPerPage} onChange={handleRowsPerPageChange}>
                    <option value={15}>15</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="dc-align-end" style={{ paddingBottom: '1px' }}>
                  <button className="dc-btn-submit" onClick={() => fetchDischargeList(1, rowsPerPage)} disabled={loading}>
                    <i className={`bi bi-arrow-clockwise ${loading ? 'dc-spinning' : ''}`}></i> {loading ? "..." : "Refresh"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="dc-records-info">
            Showing {totalRecords === 0 ? 0 : ((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalRecords)} of {totalRecords} entries
          </div>

          {/* Compact Table */}
          <div className="dc-table-wrap">
            <table className="dc-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>Sr No</th>
                  <th className="dc-text-center" style={{ width: '50px' }}>Preview</th>
                  <th>UHID</th>
                  <th>Name</th>
                  <th>Discharge Date</th>
                  <th>Bill No</th>
                  <th>Mobile No</th>
                  <th>Address</th>
                  <th className="dc-text-center">Status</th>
                  <th className="dc-text-center" style={{ width: '80px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="10" className="dc-empty">Loading records...</td></tr>
                ) : dischargeList.length === 0 ? (
                  <tr><td colSpan="10" className="dc-empty">No records found</td></tr>
                ) : (
                  dischargeList.map((d, index) => {
                    const dis = d.discharge || d; 
                    const pat = d.patient || d; 
                    const isDead = (dis.patientsSTS || '').toLowerCase() === 'dead';
                    
                    return (
                      <tr key={index}>
                        <td className="dc-text-muted">{pat.ipD_SRNO || index + 1}</td>
                        <td className="dc-text-center">
                          <button className="dc-icon-btn dc-preview" onClick={() => handlePreview(d)} title="View Receipt"><i className="bi bi-eye"></i></button>
                        </td>
                        <td>{pat.uhiD_No || pat.uhidNo || '-'}</td>
                        <td className="dc-fw-medium">{pat.name || pat.Name || '-'}</td>
                        <td>{(dis.disdate || dis.disch_Date || '').split("T")[0] || '-'}</td>
                        <td>{dis.invNo || dis.InvNo || '-'}</td>
                        <td>{pat.mobileNo || pat.MobileNo || '-'}</td>
                        <td className="dc-text-truncate" style={{ maxWidth: '150px' }} title={pat.address || pat.Address}>{pat.address || pat.Address || '-'}</td>
                        <td className="dc-text-center">
                          <span className={`dc-badge ${isDead ? 'dc-badge-danger' : 'dc-badge-success'}`}>
                            {dis.patientsSTS || (d.diS_STS ? 'Discharged' : 'Active')}
                          </span>
                        </td>
                        <td className="dc-text-center">
                          <div className="dc-btn-group">
                            <button className="dc-icon-btn dc-edit" onClick={() => handleEditClick(d)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                            <button className="dc-icon-btn dc-delete" onClick={() => handleDelete(d.discharge?.ipdid || d.ipdid)} title="Delete"><i className="bi bi-trash"></i></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {renderPagination()}

        </div>
      </div>

      {/* Modals */}
      {isEditModalOpen && recordToEdit && (
        <DischargeEditModal
          show={isEditModalOpen}
          data={recordToEdit.discharge || recordToEdit}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveEdit}
        />
      )}

      {companyDetails && showReceipt && receiptData && (
        <DischargeReceiptModal
          show={showReceipt}
          data={receiptData}
          patient={receiptData.patient}
          advanceDetails={receiptData.advanceDetails}
          dischargeDetails={receiptData} 
          serviceDetails={receiptData.services}
          roomDetails={receiptData.roomDetails}
          companyDetails={companyDetails}
          onClose={() => setShowReceipt(false)}
          onPrint={() => window.print()}
        />
      )}

      {/* ✅ PURE COMPACT CSS & COMPACT HEADER */}
      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .dc-wrap { padding: 0; font-family: system-ui, sans-serif; }
        
        .dc-card { background: #fff; border-radius: 6px; box-shadow: 0 1px 8px rgba(0,0,0,0.06); border: 1px solid #e0e6ed; overflow: visible; }
        
        /* 🚨 COMPACT HEADER 🚨 */
        .dc-header-compact { background: var(--theme-color, #0088cc); padding: 8px 15px; border-bottom: 1px solid rgba(0,0,0,0.1); border-radius: 6px 6px 0 0; display: flex; align-items: center; }
        .dc-header-brand-compact { display: flex; align-items: center; gap: 10px; color: white; }
        .dc-header-icon-compact { background: rgba(255,255,255,0.2); width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 14px; }
        .dc-header-text-compact h5 { margin: 0; font-size: 14px; font-weight: 700; line-height: 1.2; }
        .dc-header-text-compact p { margin: 0; font-size: 10px; opacity: 0.9; line-height: 1.2; margin-top: 1px; }
        
        .dc-body { padding: 10px 12px; }

        /* Filters Section */
        .dc-section { background: #f8f9fa; border: 1px solid #e0e6ed; border-radius: 5px; overflow: visible; }
        .dc-section-body { padding: 10px; }
        .dc-mb2 { margin-bottom: 10px; }
        
        .dc-checkbox-label { font-size: 11px; font-weight: 600; color: #2c3e50; display: flex; align-items: center; gap: 6px; cursor: pointer; margin: 0; }
        .dc-checkbox-label input { width: 14px; height: 14px; cursor: pointer; margin: 0; }

        .dc-filter-grid { display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-end; }
        .dc-filter-grid > div { display: flex; flex-direction: column; flex: 1; min-width: 110px; }

        .dc-label { display: block; font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 2px; }
        
        .dc-input { width: 100%; height: 26px; min-height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; }
        .dc-input:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,136,204,0.15); }
        .dc-input:disabled { background: #f3f4f6; cursor: not-allowed; }

        .dc-btn-submit { height: 26px; padding: 0 14px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 4px; width: 100%; white-space: nowrap; }
        .dc-btn-submit:hover:not(:disabled) { opacity: 0.9; }

        @keyframes dc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .dc-spinning { animation: dc-spin 1s linear infinite; }

        .dc-records-info { font-size: 10px; color: #7f8c8d; margin-bottom: 6px; font-weight: 500; }

        /* Table */
        .dc-table-wrap { overflow-x: auto; border: 1px solid #e0e6ed; border-radius: 4px; }
        .dc-table { width: 100%; border-collapse: collapse; font-size: 11px; min-width: 900px; }
        .dc-table th { background: var(--theme-color, #0088cc); color: white; padding: 6px 8px; font-weight: 600; text-align: left; white-space: nowrap; }
        .dc-table td { padding: 5px 8px; border-bottom: 1px solid #e0e6ed; vertical-align: middle; }
        .dc-table tr:hover { background: #f0f7ff; }
        
        .dc-text-center { text-align: center; }
        .dc-text-muted { color: #6c757d; }
        .dc-fw-medium { font-weight: 500; color: #2c3e50; }
        .dc-empty { text-align: center; padding: 20px; color: #7f8c8d; }
        .dc-text-truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .dc-badge { padding: 2px 6px; border-radius: 12px; font-size: 9px; font-weight: 700; text-transform: uppercase; }
        .dc-badge-success { background: #d1fae5; color: #065f46; }
        .dc-badge-danger { background: #fee2e2; color: #991b1b; }

        /* Icon Buttons */
        .dc-btn-group { display: flex; gap: 4px; justify-content: center; }
        .dc-icon-btn { width: 22px; height: 22px; border-radius: 3px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: 0.15s; }
        .dc-preview { background: #e0f2fe; color: #0284c7; }
        .dc-preview:hover { background: #0284c7; color: white; }
        .dc-edit { background: #fef9c3; color: #ca8a04; }
        .dc-edit:hover { background: #ca8a04; color: white; }
        .dc-delete { background: #fee2e2; color: #dc2626; }
        .dc-delete:hover { background: #dc2626; color: white; }

        /* Pagination */
        .dc-pagination { display: flex; justify-content: center; gap: 4px; margin-top: 10px; }
        .dc-btn-page { width: 24px; height: 24px; border: 1px solid #cbd5e0; background: white; border-radius: 4px; cursor: pointer; font-size: 11px; display: flex; align-items: center; justify-content: center; }
        .dc-btn-page:hover:not(:disabled) { background: #f1f3f5; border-color: var(--theme-color, #0088cc); color: var(--theme-color, #0088cc); }
        .dc-btn-page.active { background: var(--theme-color, #0088cc); color: white; border-color: var(--theme-color, #0088cc); }
        .dc-dots { display: flex; align-items: end; padding: 0 4px; color: #94a3b8; font-size: 14px; }

        .dc-alert { padding: 6px 10px; border-radius: 4px; font-size: 11px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .dc-alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .dc-alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }

        @media (max-width: 600px) {
          .dc-filter-grid { flex-direction: column; align-items: stretch; }
          .dc-filter-grid > div { min-width: 100% !important; }
        }
      `}</style>
    </div>
  );
};

export default DischargeList;